import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, Routes, UrlMatchResult, UrlSegment } from '@angular/router';
import { ROUTE_NAMES } from '@app/core/navigation/navigation.constants';
import { ProfileState } from '@app/layout/common/profile/state/profile.state';
import { NoonComponent } from '@vessel-reports/noon/noon.component';
import { PortComponent } from '@vessel-reports/port/port.component';
import { RobComponent } from '@vessel-reports/rob/rob.component';
import { VesselReportsAction } from '@vessel-reports/state/vessel-reports.action';
import { VesselReportsComponent } from '@vessel-reports/vessel-reports.component';
import isEqual from 'lodash-es/isEqual';
import { catchError, finalize, forkJoin, Observable, of, throwError } from 'rxjs';

const noonMatcher: (url: UrlSegment[]) => UrlMatchResult = (url: UrlSegment[]) => {
  const statusMap: Record<string, string> = {
    noon: '1,2'
  };

  const path = url[0]?.path;
  const status = statusMap[path];

  if (status) {
    let consumed = url;
    const posParams = {
      status: new UrlSegment(status, {})
    };

    if (url[1]) {
      consumed = url.slice(0, -1);
    }

    return {
      consumed,
      posParams
    };
  }

  return null;
};

const portMatcher: (url: UrlSegment[]) => UrlMatchResult = (url: UrlSegment[]) => {
  const statusMap: Record<string, string> = {
    port: '6,8'
  };

  const path = url[0]?.path;
  const status = statusMap[path];

  if (status) {
    let consumed = url;
    const posParams = {
      status: new UrlSegment(status, {})
    };

    if (url[1]) {
      consumed = url.slice(0, -1);
    }

    return {
      consumed,
      posParams
    };
  }

  return null;
};

const robMatcher: (url: UrlSegment[]) => UrlMatchResult = (url: UrlSegment[]) => {
  const statusMap: Record<string, string> = {
    'monthly-rob': '9'
  };

  const path = url[0]?.path;
  const status = statusMap[path];

  if (status) {
    let consumed = url;
    const posParams = {
      status: new UrlSegment(status, {})
    };

    if (url[1]) {
      consumed = url.slice(0, -1);
    }

    return {
      consumed,
      posParams
    };
  }

  return null;
};

const reportsRunGuardsAndResolvers: (from: ActivatedRouteSnapshot, to: ActivatedRouteSnapshot) => boolean = (
  from: ActivatedRouteSnapshot,
  to: ActivatedRouteSnapshot
) => {
  let fromCurrentRoute = from;
  while (fromCurrentRoute.firstChild) {
    fromCurrentRoute = fromCurrentRoute.firstChild;
  }

  let toCurrentRoute = to;
  while (toCurrentRoute.firstChild) {
    toCurrentRoute = toCurrentRoute.firstChild;
  }

  if (fromCurrentRoute.paramMap.get('id') && !toCurrentRoute.paramMap.get('id')) {
    return true;
  }

  const fromParams = {};
  const toParams = {};

  from.paramMap.keys.forEach((key) => {
    fromParams[key] = from.paramMap.get(key);
  });

  to.paramMap.keys.forEach((key) => {
    toParams[key] = to.paramMap.get(key);
  });

  return !isEqual(fromParams, toParams);
};

const vesselReportsResolver = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const vesselReportsAction = inject(VesselReportsAction);
  const profileState = inject(ProfileState);
  const router = inject(Router);

  const sources: Observable<any>[] = [];
  if (route.paramMap.get('status')) {
    const params = {
      type: route.paramMap.get('status'),
      vesselId: profileState.get('profile').id?.toString(),
      queryParams: !route.queryParams['filter[report_type_id]'] ? {} : route.queryParams
    };
    vesselReportsAction.actions.getVesselReports(params);
    sources.push(of({}));
  }

  return forkJoin(sources).pipe(
    finalize(() => {
      let currentRoute = route;
      while (currentRoute.firstChild) {
        currentRoute = currentRoute.firstChild;
      }
    }),
    catchError((error) => {
      console.error(error.message);

      const url = state.url.split('/').slice(0, -1).join('/') + '/' + error.pagination.lastPage;

      router.navigateByUrl(url).then();

      return throwError(() => error);
    })
  );
};

export default [
  {
    path: '',
    redirectTo: ROUTE_NAMES.NOON,
    pathMatch: 'full'
  },
  {
    path: '',
    component: VesselReportsComponent,
    children: [
      {
        component: NoonComponent,
        matcher: noonMatcher,
        runGuardsAndResolvers: reportsRunGuardsAndResolvers,
        resolve: {
          vesselReports: vesselReportsResolver
        }
      },
      {
        component: PortComponent,
        matcher: portMatcher,
        runGuardsAndResolvers: reportsRunGuardsAndResolvers,
        resolve: {
          vesselReports: vesselReportsResolver
        }
      },
      {
        component: RobComponent,
        matcher: robMatcher,
        runGuardsAndResolvers: reportsRunGuardsAndResolvers,
        resolve: {
          vesselReports: vesselReportsResolver
        }
      }
    ]
  }
] as Routes;
